import React, { useState } from "react";
import { LuCheck, LuPlus } from "react-icons/lu";
import { Skeleton, cn } from "../ui";
import { SearchField } from "./SearchField";
import { t } from "../../i18n";

export interface CatalogEntry {
  id: string;
  /** Código TUSS, mostrado acima do nome. OPME não tem. */
  code?: string;
  label: string;
}

interface CatalogListProps {
  /** null enquanto carrega. */
  items: CatalogEntry[] | null;
  addedIds: Set<string>;
  onAdd: (entry: CatalogEntry) => void;
  searchLabel: string;
  placeholder: string;
  /** Texto (ou ação) quando a busca não encontra nada. */
  empty: (query: string) => React.ReactNode;
  /** O que mostrar no item já adicionado (ex.: a quantidade). Sem isso, "Adicionado". */
  renderAdded?: (entry: CatalogEntry) => React.ReactNode;
}

// "Âncora" casa com "ancora".
const normalize = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

// Catálogo sempre visível: a busca filtra a lista, sem dropdown. Enter adiciona o primeiro resultado.
export const CatalogList: React.FC<CatalogListProps> = ({ items, addedIds, onAdd, searchLabel, placeholder, empty, renderAdded }) => {
  const [query, setQuery] = useState("");
  const q = normalize(query.trim());
  const visible = (items ?? []).filter((it) => normalize(`${it.code ?? ""} ${it.label}`).includes(q));

  const addFirst = () => {
    const first = q && visible.find((it) => !addedIds.has(it.id));
    if (first) onAdd(first);
  };

  return (
    <>
      <SearchField value={query} onChange={setQuery} placeholder={placeholder} label={searchLabel} onEnter={addFirst} />

      <div className="flex max-h-96 flex-col overflow-y-auto rounded-control border border-line bg-surface">
        {items === null ? (
          [0, 1, 2].map((i) => <Skeleton key={i} className="m-4 h-5" />)
        ) : visible.length === 0 ? (
          <p className="p-4 text-caption text-fg-muted">{empty(query)}</p>
        ) : (
          visible.map((it) => {
            const added = addedIds.has(it.id);
            return (
              <div key={it.id} className={cn("flex items-center gap-3 border-t border-line py-3 pl-4 pr-3 first:border-t-0", added && "bg-primary-tint")}>
                <p className="min-w-0 flex-1 break-words text-label">
                  {it.code && <span className="block font-mono text-caption font-semibold text-primary">{it.code}</span>}
                  {it.label}
                </p>
                {added && renderAdded ? (
                  renderAdded(it)
                ) : added ? (
                  <span className="inline-flex shrink-0 items-center gap-1.5 text-label font-semibold text-success">
                    <LuCheck size={16} aria-hidden />
                    {t("catalog.added")}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => onAdd(it)}
                    aria-label={t("catalog.addItem", { name: it.label })}
                    className="inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-button border-strong border-primary-line bg-surface px-3.5 text-label font-semibold text-primary transition-colors hover:bg-primary-tint focus-visible:outline-none focus-visible:shadow-focus"
                  >
                    <LuPlus size={16} aria-hidden />
                    {t("catalog.add")}
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>
    </>
  );
};
